// src/components/auth/PasswordChecklist.js
/**
 * Pop‑over checklist that ticks off password requirements in real time.
 *
 * Render this **inside a `relative` parent** (typically the password-input
 * wrapper) so its `absolute bottom-full mb-2` positioning anchors it just
 * above the field—crucial on mobile where the on‑screen keyboard can hide
 * content below.
 *
 * Visual logic
 * ─────────────
 * Each requirement turns from a red ❌ to a green ✅ when its boolean in the
 * `criteria` prop becomes `true`. We rely on the parent component (e.g., the
 * `usePasswordCriteria` hook in <SignUp/>) to compute those booleans.
 *
 * @typedef {object} PasswordChecklistCriteria
 * @property {boolean} length
 *   `true` when the password satisfies the minimum length rule.
 * @property {boolean} upper
 *   `true` when the password contains an uppercase letter.
 * @property {boolean} lower
 *   `true` when the password contains a lowercase letter.
 * @property {boolean} number
 *   `true` when the password contains a numeric character.
 * @property {boolean} special
 *   `true` when the password contains a non-alphanumeric character.
 *
 * @param {object} props
 * @param {Function} props.t
 *   Translation function from `react-i18next`.
 * @param {PasswordChecklistCriteria} props.criteria
 *   Live password-validation flags, typically from `usePasswordCriteria`.
 *
 * @returns {JSX.Element}
 */
export const Checklist = ({ t, criteria }) => (
  <div
    className="absolute bottom-full mb-2 left-0 popover"
    onMouseDown={(e) => e.preventDefault()}
  >
    <p className="mb-2 font-medium text-typography">
      {t("passwordChecklist.title")}
    </p>
    <ul className="space-y-1">
      {[
        ["length", t("passwordChecklist.length")],
        ["upper", t("passwordChecklist.upper")],
        ["lower", t("passwordChecklist.lower")],
        ["number", t("passwordChecklist.number")],
        ["special", t("passwordChecklist.special")],
      ].map(([key, label]) => {
        const ok = criteria[key];
        return (
          <li key={key} className="flex items-center gap-2">
            {ok ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 fill-green-500"
                viewBox="0 -960 960 960"
              >
                <path d="m424-296 282-282-56-56-226 226-114-114-56 56zm56 216q-83 0-156-31.5T197-197t-85.5-127T80-480t31.5-156T197-763t127-85.5T480-880t156 31.5T763-763t85.5 127T880-480t-31.5 156T763-197t-127 85.5T480-80m0-80q134 0 227-93t93-227-93-227-227-93-227 93-93 227 93 227 227 93m0-320"></path>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 fill-red-500"
                viewBox="0 -960 960 960"
              >
                <path d="M330-120 120-330v-300l210-210h300l210 210v300L630-120zm36-190 114-114 114 114 56-56-114-114 114-114-56-56-114 114-114-114-56 56 114 114-114 114zm-2 110h232l164-164v-232L596-760H364L200-596v232zm116-280"></path>
              </svg>
            )}
            <span className={`${ok ? "text-green-500" : "text-red-500"}`}>
              {label}
            </span>
          </li>
        );
      })}
    </ul>
  </div>
);
